import { useEffect, useRef, useState } from "react"

import { useMutation, useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import {
  previewTenderHandoffEmail,
  previewTenderSummaryEmail,
  searchTenderSummaryRecipients,
  sendTenderSummaryEmail,
  type TenderSummaryPreview,
  type TenderSummaryRecipient,
} from "@/api/tender-api"
import { Eye, Loader2, Mail, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"

import { serverErrorMessage } from "../utils/server-error-message"

type Props = {
  caseId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onBack?: () => void
}

export function TenderSummaryEmailDialog({ caseId, open, onOpenChange, onBack }: Props) {
  const { t } = useTranslation()
  const [query, setQuery] = useState("")
  const [debouncedQuery, setDebouncedQuery] = useState("")
  const [recipients, setRecipients] = useState<TenderSummaryRecipient[]>([])
  const [note, setNote] = useState("")
  const [preview, setPreview] = useState<
    | (Omit<TenderSummaryPreview, "recipient" | "caseLinkIncluded"> &
        Partial<Pick<TenderSummaryPreview, "recipient" | "caseLinkIncluded">>)
    | null
  >(null)
  const [status, setStatus] = useState("")
  const [error, setError] = useState("")

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300)
    return () => clearTimeout(timer)
  }, [query])

  const search = useQuery({
    queryKey: ["tender-summary-recipients", caseId, debouncedQuery],
    queryFn: () => searchTenderSummaryRecipients(caseId, debouncedQuery),
    enabled: open && debouncedQuery.length >= 2,
    retry: false,
    staleTime: 60_000,
  })
  const send = useMutation({
    mutationFn: () =>
      sendTenderSummaryEmail(caseId, {
        recipientIds: recipients.map((r) => r.oid),
        note: note.trim() || undefined,
      }),
    retry: false,
  })
  const previewRequest = useMutation({
    mutationFn: (oid?: string) =>
      oid
        ? previewTenderSummaryEmail(caseId, {
            recipientIds: [oid],
            note: note.trim() || undefined,
          })
        : previewTenderHandoffEmail(caseId, { summaryOnly: true, note: note.trim() || undefined }),
    retry: false,
  })
  const busy = send.isPending || previewRequest.isPending

  // The draft (recipients, note, preview, outcome) belongs to one open session
  // on one case. TenderHandoffDialog keeps this component mounted while
  // closed and the case page is reused across :caseId, so without this a
  // sender could close on case A, open on case B and send A's recipients B's
  // summary. Reset on every close and whenever the case changes; failed
  // recipients are retained only within the current open session.
  // Bumped on every reset so a preview request started in an earlier dialog
  // context (before a close or a case change) is ignored when it completes.
  const previewGenerationRef = useRef(0)
  const reset = () => {
    previewGenerationRef.current += 1
    setQuery("")
    setDebouncedQuery("")
    setRecipients([])
    setNote("")
    setPreview(null)
    setStatus("")
    setError("")
  }
  const [draftCaseId, setDraftCaseId] = useState(caseId)
  if (draftCaseId !== caseId) {
    setDraftCaseId(caseId)
    reset()
  }
  // Also reset when the parent closes the dialog without going through
  // ``close`` below (derived state, no effect), so an external close cannot
  // keep a draft or let an in-flight preview land on the next open.
  const [wasOpen, setWasOpen] = useState(open)
  if (wasOpen !== open) {
    setWasOpen(open)
    if (!open) reset()
  }
  const close = (next: boolean) => {
    if (busy) return
    if (!next) reset()
    onOpenChange(next)
  }

  const showPreview = async (oid?: string) => {
    setError("")
    const generation = previewGenerationRef.current
    try {
      const result = await previewRequest.mutateAsync(oid)
      if (previewGenerationRef.current !== generation) return // reset meanwhile
      setPreview(result)
    } catch (err) {
      if (previewGenerationRef.current !== generation) return
      setError(serverErrorMessage(err, t("tender.summaryEmail.previewError")))
    }
  }
  const submit = async () => {
    setError("")
    setStatus("")
    try {
      const result = await send.mutateAsync()
      setRecipients((current) => current.filter((r) => result.failedRecipientIds.includes(r.oid)))
      setStatus(
        t("tender.summaryEmail.result", {
          sent: result.sentRecipientIds.length,
          failed: result.failedRecipientIds.length,
        })
      )
    } catch (err) {
      setError(serverErrorMessage(err, t("tender.summaryEmail.sendError")))
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="size-5" />
            {t("tender.summaryEmail.title")}
          </DialogTitle>
          <DialogDescription>{t("tender.summaryEmail.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="summary-recipient-search">{t("tender.summaryEmail.recipients")}</Label>
            <Input
              disabled={busy || recipients.length >= 50}
              id="summary-recipient-search"
              maxLength={100}
              placeholder={t("tender.summaryEmail.searchPlaceholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {search.isFetching && (
              <Loader2 aria-label={t("common.loading")} className="size-4 animate-spin" />
            )}
            {search.isError && (
              <p className="text-destructive text-sm" role="alert">
                {t("tender.summaryEmail.searchError")}
              </p>
            )}
            {debouncedQuery.length >= 2 &&
              !search.isFetching &&
              !search.isError &&
              search.data?.length === 0 && (
                <p className="text-muted-foreground text-sm">
                  {t("tender.summaryEmail.noResults")}
                </p>
              )}
            {query.trim().length >= 2 &&
              query.trim() === debouncedQuery &&
              search.data
                ?.filter((r) => !recipients.some((s) => s.oid === r.oid))
                .map((r) => (
                  <Button
                    key={r.oid}
                    className="h-auto w-full justify-start text-left whitespace-normal"
                    disabled={busy || recipients.length >= 50}
                    variant="ghost"
                    onClick={() => {
                      setRecipients((current) => [...current, r])
                      setQuery("")
                      setStatus("")
                    }}
                  >
                    <span>
                      {r.displayName}
                      <span className="text-muted-foreground block text-xs">{r.email}</span>
                    </span>
                  </Button>
                ))}
          </div>
          <div className="max-h-48 space-y-2 overflow-y-auto">
            {recipients.map((r) => (
              <div key={r.oid} className="bg-muted/40 flex items-center gap-2 rounded-md px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{r.displayName}</p>
                  <p className="text-muted-foreground truncate text-xs">{r.email}</p>
                </div>
                <Button
                  aria-label={t("tender.summaryEmail.previewFor", { name: r.displayName })}
                  disabled={busy}
                  size="icon"
                  variant="ghost"
                  onClick={() => void showPreview(r.oid)}
                >
                  <Eye className="size-4" />
                </Button>
                <Button
                  aria-label={t("tender.summaryEmail.remove", { name: r.displayName })}
                  disabled={busy}
                  size="icon"
                  variant="ghost"
                  onClick={() => setRecipients((current) => current.filter((x) => x.oid !== r.oid))}
                >
                  <X className="size-4" />
                </Button>
              </div>
            ))}
          </div>
          <p className="text-muted-foreground text-xs">{t("tender.summaryEmail.linkHint")}</p>
          <div className="space-y-2">
            <Label htmlFor="summary-note">{t("tender.handoff.noteLabel")}</Label>
            <Textarea
              disabled={busy}
              id="summary-note"
              maxLength={4000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          {error && (
            <p className="text-destructive text-sm" role="alert">
              {error}
            </p>
          )}
          {status && (
            <p className="text-sm" role="status">
              {status}
            </p>
          )}
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button disabled={busy} variant="outline" onClick={() => void showPreview()}>
            <Eye className="size-4" />
            {t("tender.handoff.preview")}
          </Button>
          <div className="grid grid-cols-1 gap-2 sm:flex sm:justify-end">
            <Button disabled={busy} variant="outline" onClick={onBack ?? (() => close(false))}>
              {t(onBack ? "tender.summaryEmail.back" : "tender.handoff.close")}
            </Button>
            <Button disabled={busy || recipients.length === 0} onClick={() => void submit()}>
              {send.isPending ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <Mail className="mr-2 size-4" />
              )}
              {t("tender.summaryEmail.send", { count: recipients.length })}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
      <Dialog
        open={!!preview}
        onOpenChange={(next) => {
          if (!next) setPreview(null)
        }}
      >
        <DialogContent className="h-[90vh] max-w-4xl grid-rows-[auto_1fr]">
          <DialogHeader>
            <DialogTitle>{preview?.subject}</DialogTitle>
            <DialogDescription>
              {preview?.recipient && <>{preview.recipient.displayName} — </>}
              {t(
                preview?.caseLinkIncluded
                  ? "tender.summaryEmail.linkIncluded"
                  : "tender.summaryEmail.linkOmitted"
              )}
            </DialogDescription>
          </DialogHeader>
          <iframe
            className="h-full min-h-0 w-full rounded-md border bg-white"
            sandbox=""
            srcDoc={preview?.html}
            title={t("tender.handoff.previewTitle")}
          />
        </DialogContent>
      </Dialog>
    </Dialog>
  )
}
